import PropTypes from 'prop-types'
import React, { forwardRef, useEffect, useState } from 'react'
import { createUseStyles } from 'react-jss'
import { useTranslation } from 'react-i18next'

import GlyphIcon from 'components/base/GlyphIcon'
import Text from 'components/typography/Text'
import Tooltip from 'components/popovers/Tooltip'
import useNetworkConnection from 'hooks/useNetworkConnection'

const useStyles = createUseStyles(({ colors }) => {
  const warningStyles = backgroundColor => props => ({
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    background: backgroundColor,
    padding: '4px 8px',
    borderRadius: props.rotateBorder ? '0 0 12px 12px' : '12px 12px 0 0'
  })

  return {
    unstableWarning: warningStyles(colors.accentYellow),
    noNetworkWarning: warningStyles(colors.negative),
    tooltip: {
      width: '60%'
    },
    banner: props => ({
      marginTop: props.top || 0,
      position: props.position,
      width: '100%',
      zIndex: 1,
      marginBottom: props.bottom || 0
    })
  }
})

const NetworkBanner = forwardRef(
  ({ rotateBorder, top, position, bottom }, ref) => {
    const { t } = useTranslation()
    const { isOnline, isStable, effectiveType } = useNetworkConnection()
    const [unstableWarningVisible, setUnStableWarningVisible] = useState(false)
    const [offlineWarningVisible, setOfflineWarningVisible] = useState(false)

    const classes = useStyles({
      rotateBorder,
      top,
      position,
      bottom: !ref?.current && !isStable ? -10 : bottom
    })

    useEffect(() => {
      const quill = ref?.current?.getEditor() || window?.quillInstance
      if (quill) {
        const { root } = quill

        if (!isStable && !(unstableWarningVisible || offlineWarningVisible)) {
          root.style.marginTop = '20px'
        } else {
          root.style.marginTop = '0'
        }
      }
      quill?.focus()
    }, [isStable, ref, window?.quillInstance, unstableWarningVisible, offlineWarningVisible])

    useEffect(() => {
      if (unstableWarningVisible) setUnStableWarningVisible(false)

      if (offlineWarningVisible) setOfflineWarningVisible(false)
    }, [isStable, isOnline, effectiveType])

    if (unstableWarningVisible) return null

    if (offlineWarningVisible) return null

    if (isOnline && !isStable) {
      return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div className={classes.banner} onClick={() => { setUnStableWarningVisible(true) }}>
          <div className={classes.unstableWarning}>
            <GlyphIcon glyph="warning" size="medium" />
            <Text size="xSmall" weight="medium" color="primaryDark">
              {t('networkConnection.unStatbleInternet.title')}
            </Text>
            <Tooltip
              className={classes.tooltip}
              titleSize="xxSmall"
              delay={1000}
              title={t('networkConnection.unStatbleInternet.description')}
            >
              <Text
                as="a"
                size="xSmall"
                weight="medium"
                color="primaryDark"
                decoration="underline"
              >
                {t('networkConnection.learnMore')}
              </Text>
            </Tooltip>
          </div>
        </div>
      )
    }

    if (!isOnline && !isStable) {
      return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div className={classes.banner} onClick={() => { setOfflineWarningVisible(true) }}>
          <div className={classes.noNetworkWarning}>
            <GlyphIcon glyph="unavailable" size="medium" />
            <Text size="xSmall" weight="medium" color="light">
              {t('networkConnection.noInternet.title')}
            </Text>
            <Tooltip
              className={classes.tooltip}
              titleSize="xxSmall"
              delay={1000}
              title={t('networkConnection.noInternet.description')}
            >
              <Text
                as="a"
                size="xSmall"
                weight="medium"
                color="light"
                decoration="underline"
              >
                {t('networkConnection.learnMore')}
              </Text>
            </Tooltip>
          </div>
        </div>
      )
    }

    return null
  }
)

NetworkBanner.prototype = {
  rotateBorder: PropTypes.bool,
  position: PropTypes.oneOf(['absolute', 'relative'])
}
NetworkBanner.defaultProps = {
  rotateBorder: false,
  position: 'absolute'
}

export default NetworkBanner
